import * as turf from '@turf/turf';
import useElevationRequest from './useElevationRequest';
import { getPolylineElevations } from '../../services/elevation/ElevationService';

const MAX_LINE_SAMPLES = 400;
const LINE_SAMPLE_SPACING_METERS = 25;

function toCoordinates(points) {
    if (!Array.isArray(points)) {
        return [];
    }
    return points
        .filter(
            (point) =>
                Array.isArray(point) &&
                Number.isFinite(point[0]) &&
                Number.isFinite(point[1]),
        )
        .map(([longitude, latitude]) => ({ latitude, longitude }));
}

function hasDistinctCoordinates(coordinates, minimum) {
    return (
        coordinates.length >= minimum &&
        new Set(
            coordinates.map(
                ({ latitude, longitude }) => `${latitude},${longitude}`,
            ),
        ).size >= minimum
    );
}

function segmentLengthMeters(start, end) {
    return (
        turf.distance(
            [start.longitude, start.latitude],
            [end.longitude, end.latitude],
            { units: 'kilometers' },
        ) * 1000
    );
}

// Allocate samples across measure segments so each vertex is an endpoint
// (no chord cutting corners) while staying within MAX_LINE_SAMPLES.
function segmentSampleCounts(coordinates) {
    const lengths = [];
    let totalLength = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
        const length = segmentLengthMeters(coordinates[i], coordinates[i + 1]);
        lengths.push(Math.max(length, 0));
        totalLength += Math.max(length, 0);
    }
    if (!lengths.length) {
        return [];
    }

    const desiredTotal = Math.max(
        lengths.length * 2,
        Math.min(
            MAX_LINE_SAMPLES,
            Math.round(totalLength / LINE_SAMPLE_SPACING_METERS) + 1,
        ),
    );
    const counts = lengths.map(() => 2);
    let remaining = Math.max(0, desiredTotal - counts.length * 2);
    if (remaining > 0 && totalLength > 0) {
        const extras = lengths.map((length) => (length / totalLength) * remaining);
        const floors = extras.map((value) => Math.floor(value));
        floors.forEach((value, index) => {
            counts[index] += value;
            remaining -= value;
        });
        const order = extras
            .map((value, index) => ({ index, frac: value - Math.floor(value) }))
            .sort((a, b) => b.frac - a.frac);
        for (let i = 0; i < remaining; i++) {
            counts[order[i % order.length].index] += 1;
        }
    }
    return counts;
}

function withDistanceAlong(samples) {
    let distanceAlong = 0;
    return samples.map((sample, index) => {
        if (index > 0) {
            const previous = samples[index - 1];
            distanceAlong += segmentLengthMeters(previous, sample);
        }
        return {
            ...sample,
            distanceAlong,
            distance_along: distanceAlong,
            height_msl: sample.elevations?.MSL,
        };
    });
}

function joinSegmentProfiles(profiles) {
    const samples = [];
    for (let index = 0; index < profiles.length; index++) {
        const profile = profiles[index] || [];
        if (!profile.length) {
            continue;
        }
        if (!samples.length) {
            samples.push(...profile);
            continue;
        }
        // Shared vertex already came from the previous segment endpoint.
        samples.push(...profile.slice(1));
    }
    return withDistanceAlong(samples);
}

function requestFor(coordinates) {
    if (!hasDistinctCoordinates(coordinates, 2)) {
        return null;
    }
    return async () => {
        const sampleCounts = segmentSampleCounts(coordinates);
        const polylines = sampleCounts.map((totalSamples, index) => ({
            coordinates: [coordinates[index], coordinates[index + 1]],
            totalSamples,
        }));
        const result = await getPolylineElevations(polylines);
        return {
            unit: result.unit,
            samples: joinSegmentProfiles(result.profiles),
        };
    };
}

export default function useMeasureElevation({
    coordinates: points = [],
    enabled = true,
} = {}) {
    const showElevation = !!enabled;
    const geometryKey = points.length ? JSON.stringify(points) : '';
    const coordinates = toCoordinates(points);
    const request = showElevation ? requestFor(coordinates) : null;
    const elevation = useElevationRequest(
        request,
        `${showElevation}:${geometryKey}`,
    );

    return {
        status: elevation.status,
        samples: elevation.status === 'success' ? elevation.data?.samples || [] : [],
        unit: elevation.status === 'success' ? elevation.data?.unit || null : null,
        error: elevation.error,
    };
}
